import React from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { HStack, Heading, VStack, Text } from '@rtkwlf/fenrir-react';
import { faClose } from '@rtkwlf/pro-solid-svg-icons/faClose';
import {
  SpidrImprovement,
  type SpidrWithFocus,
  type ConciergeTier,
  type SecurityFocusLite,
} from '@rtkwlf/track-public';
import { useQuery } from '@tanstack/react-query';

import { useUpdateOrganizationSpidrRating } from '@apis/track-public/mutations';
import { useUpdateOrganizationSpidrImprovements } from '@apis/track-public/mutations';
import { useGetOrganizationOptions } from '@apis/track-public/query-options';
import { Alert, AlertTitle } from '@packages/fenrir/components/alert';
import { Button } from '@packages/fenrir/components/button';
import { Chip } from '@packages/fenrir/components/chip';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
} from '@packages/fenrir/components/dialog';
import { IconButton } from '@packages/fenrir/components/icon-button';
import { Rating } from '@packages/fenrir/components/rating';
import { Snackbar } from '@packages/fenrir/components/snackbar';
import { Stack } from '@packages/fenrir/components/stack';
import { SNACKBAR_DURATION_SHORT } from 'ae-constants/snackbar';

import { ConciergeTierBadge } from '../components/concierge-tier-badge/concierge-tier-badge';
import { routes } from '../constants/routes';
import { useSecurityJourneyTranslations } from '../locales/use-security-journey-translations';

import { useFormatNullableDate } from './formatted-date';
import { InfoField } from './info-field';
import {
  SpidrStateBadge,
  type SpidrStateBadgeProps,
} from './spidr-state-badge';

type SpidrDetailsHeaderProps = {
  id: string;
  version: number;
  name: string;
  state: string;
  focus: SecurityFocusLite | null;
  ticketId: string;
  deliveredAt: Date | null;
  expiryDate: Date | null;
  tier: ConciergeTier;
  rating: number | null;
  improvements: SpidrImprovement[];
};

type ToastState = {
  isOpen: boolean;
  severity: 'success' | 'error';
  title: string;
  description: string;
};

export const SpidrDetailsHeader: React.FC<SpidrDetailsHeaderProps> = ({
  id,
  version,
  name,
  state,
  ticketId,
  focus,
  deliveredAt,
  expiryDate,
  tier,
  rating,
  improvements,
}) => {
  const { t } = useSecurityJourneyTranslations();

  const internalOnly = ticketId === 'internal';

  const { data: organization } = useQuery(useGetOrganizationOptions());

  const isOrganizationTierNonLegacy = React.useMemo(() => {
    return organization?.tierInfo && organization.tierInfo.legacy === false;
  }, [organization]);

  const blankToast: ToastState = {
    isOpen: false,
    severity: 'success',
    title: '',
    description: '',
  };

  const [toastState, setToastState] = React.useState<ToastState>(blankToast);

  const [isDialogOpen, setIsDialogOpen] = React.useState(false);

  const onSuccessUpdateSpidrRating = (updatedSpidr: SpidrWithFocus) => {
    if (updatedSpidr.rating !== 5) {
      setIsDialogOpen(true);
    }
  };

  /**
   * init mutation to update spidr rating
   */
  const { mutate: updateOrganizationSpidrRating } =
    useUpdateOrganizationSpidrRating(id, t);

  const handleUpdateOrganizationSpidrRating = React.useCallback(
    (updatedRating: number | null) => {
      if (organization && updatedRating) {
        updateOrganizationSpidrRating(
          {
            orgID: organization.id,
            ifMatch: String(version),
            spidrID: id,
            spidrRatingInput: { rating: updatedRating },
          },
          {
            onSuccess: (updatedSpidr) =>
              onSuccessUpdateSpidrRating(updatedSpidr),
            onError: (err) =>
              setToastState({
                isOpen: true,
                severity: 'error',
                title: t('spidrID.rating.update.error.title'),
                description: t('spidrID.rating.update.error.description'),
              }),
          },
        );
      }
    },
    [organization, updateOrganizationSpidrRating, version, id, t],
  );

  const allImprovements: SpidrImprovement[] = [
    SpidrImprovement.BetterCommunicationClarityOfFindings,
    SpidrImprovement.BetterFollowUpSupport,
    SpidrImprovement.FasterCompletionTime,
    SpidrImprovement.MoreActionableInsights,
    SpidrImprovement.MoreDetailedAssessment,
    SpidrImprovement.MorePersonalizedRecommendations,
    SpidrImprovement.MorePracticalClearRecommendations,
  ];

  const [selectedImprovements, setSelectedImprovements] =
    React.useState<SpidrImprovement[]>(improvements);

  const handleChipClick = (improvement: SpidrImprovement) => {
    if (selectedImprovements.includes(improvement)) {
      setSelectedImprovements((prev) => prev.filter((i) => i !== improvement));
    } else {
      setSelectedImprovements((prev) => [...prev, improvement]);
    }
  };

  const areImprovementsEqual = React.useMemo(() => {
    return (
      selectedImprovements.length === improvements.length &&
      selectedImprovements.every((improvement) =>
        improvements.includes(improvement),
      )
    );
  }, [improvements, selectedImprovements]);

  /**
   * init mutation to update spidr improvements
   */
  const {
    mutate: updateOrganizationSpidrImprovements,
    error: updateOrganizationSpidrImprovementsError,
  } = useUpdateOrganizationSpidrImprovements(id, t);

  const handleUpdateOrganizationSpidrImprovements = React.useCallback(() => {
    if (organization) {
      updateOrganizationSpidrImprovements(
        {
          orgID: organization.id,
          ifMatch: String(version),
          spidrID: id,
          spidrImprovementsInput: { improvements: selectedImprovements },
        },
        {
          onSuccess: () => {
            setIsDialogOpen(false);
            setToastState({
              isOpen: true,
              severity: 'success',
              title: t('spidrID.improvements.update.success.title'),
              description: t('spidrID.improvements.update.success.description'),
            });
          },
        },
      );
    }
  }, [
    organization,
    updateOrganizationSpidrImprovements,
    version,
    id,
    selectedImprovements,
    t,
  ]);

  return (
    <VStack width="full" gap="xxlarge">
      <HStack>
        <Heading level="1" styledAs="heading3">
          {name}
        </Heading>
        <SpidrStateBadge state={state as SpidrStateBadgeProps['state']} />
      </HStack>
      <HStack gap="medium">
        <InfoField
          name={t('spidrID.Ticket')}
          value={
            internalOnly ? t('spidrID.internalOnly') : t('spidrID.viewTicket')
          }
          url={
            internalOnly
              ? undefined
              : routes.tickets.ticketID.index({ ticketID: ticketId })
          }
        />
        {focus && (
          <InfoField
            name={t('spidrID.securityFocus')}
            value={focus.name}
            url={routes.securityFocuses.focusID.index({ focusID: focus.id })}
          />
        )}
        {isOrganizationTierNonLegacy && (
          <VStack gap="small">
            <Text styledAs="body4">{t('spidrID.tier')}</Text>
            <ConciergeTierBadge.Spidr tier={tier} />
          </VStack>
        )}
        <InfoField
          name={t('spidrID.dateDelivered')}
          value={useFormatNullableDate(deliveredAt)}
        />
        <InfoField
          name={t('spidrID.expiry')}
          value={useFormatNullableDate(expiryDate)}
        />
        <VStack gap="small">
          <Text styledAs="body4">{t('spidrID.rating.label')}</Text>
          <Rating
            data-testid="spidr-rating"
            name="spidr-rating"
            value={rating ?? 0}
            onChange={(_, newValue) =>
              handleUpdateOrganizationSpidrRating(newValue)
            }
          />
        </VStack>
        <Dialog open={isDialogOpen} onClose={() => setIsDialogOpen(false)}>
          <IconButton
            onClick={() => setIsDialogOpen(false)}
            sx={(theme) => ({
              position: 'absolute',
              right: 0,
              top: 0,
              color: theme.palette.grey[500],
            })}
          >
            <FontAwesomeIcon icon={faClose} />
          </IconButton>
          <DialogContent>
            <Stack gap={2}>
              <DialogContentText>
                {t('spidrID.improvements.label')}
              </DialogContentText>
              <Stack direction="row" flexWrap="wrap" gap={1}>
                {allImprovements.map((improvement) => {
                  return (
                    <Chip
                      key={improvement}
                      label={improvement}
                      variant={
                        selectedImprovements.includes(improvement)
                          ? 'filled'
                          : 'outlined'
                      }
                      color="info"
                      size="medium"
                      onClick={() => handleChipClick(improvement)}
                    />
                  );
                })}
              </Stack>
              {updateOrganizationSpidrImprovementsError && (
                <Alert severity="error">
                  <AlertTitle>
                    {t('spidrID.improvements.update.error.title')}
                  </AlertTitle>
                  {t('spidrID.improvements.update.error.description')}
                </Alert>
              )}
            </Stack>
          </DialogContent>
          {!areImprovementsEqual && (
            <DialogActions>
              <Button onClick={() => setIsDialogOpen(false)}>
                {t('common.cancel')}
              </Button>
              <Button onClick={handleUpdateOrganizationSpidrImprovements}>
                {t('common.submit')}
              </Button>
            </DialogActions>
          )}
        </Dialog>
        <Snackbar
          open={toastState.isOpen}
          onClose={() => setToastState(blankToast)}
          autoHideDuration={
            toastState.severity === 'success' ? SNACKBAR_DURATION_SHORT : null
          }
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert
            severity={toastState.severity}
            onClose={() => setToastState(blankToast)}
          >
            <AlertTitle>{toastState.title}</AlertTitle>
            {toastState.description}
          </Alert>
        </Snackbar>
      </HStack>
    </VStack>
  );
};
